<script lang="ts" setup>
import { Card, Icon, Tooltip } from '@skillstown/leap';
import { Role } from '~~/data/enums/Role';
import { NavigationGroup, NavigationItem } from '~~/data/models/NavigationItem';
import { RoutePermissions } from '~~/data/RoutePermissions';
import { useUser } from '~~/stores/user';

interface IProps {
  main: NavigationGroup[];
  smallLinks?: NavigationItem[];
}

const props = defineProps<IProps>();

const { cardPadding } = useLeapCustomizations();
const user = useUser();
const { width } = useViewport();

const filteredGroups = computed(() =>
  props.main.filter(group => filterNavigationItems(group.items || []).length > 0 &&
    (
      (group.category === 'PA' && (user.isPAEnabled || user.role === Role.TripleAdmin)) ||
      (group.category === 'PE' && (user.isPEEnabled || user.role === Role.TripleAdmin)) ||
      (group.category !== 'PA' && group.category !== 'PE')
    ))
);

function filterNavigationItems(navigationItems: NavigationItem[]): NavigationItem[] {
  return navigationItems.filter((link) =>
    link.permissions == null ? true : RoutePermissions.hasPermissions(link.permissions)
  );
}

// While there should be built in functionality for this. Active class is not being applied to child routes.
function isRouteActive(navigationPath: string): boolean {
  const route = useRoute();
  const isRoot = navigationPath === '/admin/dashboard' || navigationPath === '/dashboard';

  return isRoot ? false : route.path.includes(navigationPath);
}
</script>

<template>
  <nav class="app-sidebar">
    <Card class="app-sidebar__card divide-y divide-gray-300" :padding="cardPadding">
        <NuxtLink to="/" v-if="width < 1024">
          <img src="@/assets/images/nibe-logo.svg" width="80" class="pl-2" />
        </NuxtLink>
      <div v-for="linkGroup in filteredGroups" :key="linkGroup.title">
        <h3 class="card__group-title">{{ linkGroup.title }}</h3>
        <NuxtLink v-for="link in filterNavigationItems(linkGroup.items || [])" :key="link.name" :to="link.to"
          class="card__route-link" :class="{ 'card__route-link--active': isRouteActive(link.to) }">
          <div class="flex items-center">
            <div v-if="link.customIcon" class="w-[40px] h-[40px] p-[7px] flex justify-center items-center">
              <img :src="link.customIcon" />
            </div>
            <Icon v-else :icon="link.icon" variant="ghost" color="black" size="l" />
            <span class="inline-block">{{ link.name }}</span>
          </div>
        </NuxtLink>
      </div>
    </Card>
  </nav>
</template>

<style lang="postcss" scoped>
.app-sidebar {
  @apply w-full;
  @apply h-full;

  ::-webkit-scrollbar-track {
    @apply hidden;
  }

  ::-webkit-scrollbar-thumb {
    @apply hidden;
  }

  ::-webkit-scrollbar-thumb:hover {
    @apply hidden;
  }

  .app-sidebar__card {
    @apply w-full;
    @apply h-full;
    @apply space-y-4;
    @apply p-2;
    @apply overflow-y-auto;
    @apply rounded-none;
    @apply lg:rounded-xl;

    .card__group-title {
      @apply pl-3;
      @apply my-3;
      @apply font-semibold;
    }

    .card__route-link {
      @apply relative flex items-center;
      @apply px-0 pl-7;

      :deep(svg) {
        color: var(--color-grey-pure);
        @apply transition duration-200 ease-in-out;
      }

      :deep(img) {
        filter: invert(30%) sepia(33%) saturate(426%) hue-rotate(169deg) brightness(94%) contrast(86%);
      }

      :deep(span) {
        @apply pl-4 transition duration-200 ease-in-out;
        color: var(--color-grey-pure);
        font-size: var(--font-size-xs);
      }

      &--active {
        &::after {
          content: '';
          width: 2px;
          background-color: var(--color-brand);
          @apply absolute bottom-0 left-0 top-0 transition duration-200 ease-in-out;
        }

        :deep(svg) {
          color: var(--color-brand);
        }

        :deep(img) {
          filter: invert(27%) sepia(84%) saturate(1808%) hue-rotate(239deg) brightness(77%) contrast(93%);
        }

        :deep(span) {
          color: var(--color-brand);
        }
      }
    }

    .router-link-exact-active {
      &::after {
        content: '';
        width: 2px;
        background-color: var(--color-brand);
        @apply absolute bottom-0 left-0 top-0 transition duration-200 ease-in-out;
      }

      :deep(svg) {
        color: var(--color-brand);
      }

      :deep(span) {
        color: var(--color-brand);
      }
    }

    /* .app-sidebar__icon {
        @apply hidden xl:inline-block;
      } */
    .app-sidebar__tooltip {
      @apply xl:hidden;

      :deep(.popper) {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: center;
        height: 2rem;
        font-size: 12px;
        width: 6.2rem;
        margin-left: 1rem;
      }
    }
  }
}
</style>
